import { sanitize } from '@ben/common/dom';
import RiCloseLine from '@ben/icons/close-line';
import { TagColorVariant } from '@ben/orion/constants/display-status';
import { IconName } from '@ben/orion/icon';
import IconButton from '@ben/orion/icon-button';
import InlineTextButton from '@ben/orion/inline-text-button';
import Tag, { TagVariant } from '@ben/orion/tag';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { fsMaskForHSBCInstance } from '@ben/shared/constants';
import { useTranslation } from 'react-i18next';
import styled, { css, useTheme } from 'styled-components';
import { useNavigateProfileDependents } from '../../use-navigate-profile-dependents/use-navigate-profile-dependents';
import { type ContactOption } from '../use-contact-selection';

interface ContactCardHeaderProps {
  contact: ContactOption;
  disabled?: boolean;
  hasMissingRequiredFields: boolean;
  hasUnsavedChanges: boolean;
  onRemove: (key: string) => void;
  requiredContactFields: string[];
}

const ContactCardHeader = ({
  contact,
  disabled = false,
  hasMissingRequiredFields,
  hasUnsavedChanges,
  onRemove,
  requiredContactFields,
}: ContactCardHeaderProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { navigateEditDependent } = useNavigateProfileDependents({
    requiredContactFields,
  });

  const onClickEdit = () => {
    navigateEditDependent(contact.key);
  };

  const hasDescription = Boolean(contact.description);
  const statusTagLabel = hasMissingRequiredFields
    ? t('contacts.missingRequiredFields')
    : hasUnsavedChanges
      ? t('contacts.unsavedChanges')
      : t('contacts.assigned');
  const statusTagColor =
    hasMissingRequiredFields || hasUnsavedChanges
      ? TagColorVariant.Error
      : TagColorVariant.Success;

  return (
    <Container>
      <TextContainer hasDescription={hasDescription} $disabled={disabled}>
        <Typography
          variant={TypographyVariant.h3}
          className={fsMaskForHSBCInstance}
        >
          {contact.label}
        </Typography>
        {contact.description && (
          <Typography
            variant={TypographyVariant.bodyLg1}
            color={theme.palette.neutral[90]}
            dangerouslySetInnerHTML={{ __html: sanitize(contact.description) }}
            id={`multi-select-option-${contact.key}`}
          />
        )}
        <InlineTextButton
          label={t('words.edit')}
          iconName={IconName.Edit}
          isLeftIcon
          onClick={onClickEdit}
        />
      </TextContainer>
      <TagWrapper>
        <Tag
          name={statusTagLabel}
          variant={TagVariant.Outlined}
          colorVariant={statusTagColor}
        />
        {!disabled && (
          <IconButton
            icon={RiCloseLine}
            onClick={() => onRemove(contact.key)}
            size="lg"
          />
        )}
      </TagWrapper>
    </Container>
  );
};

export default ContactCardHeader;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    padding: ${theme.spacing(3)};
    background: ${theme.palette.neutral[40]};
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid ${theme.palette.neutral[60]};
    border-bottom: none;
    border-radius: 4px 4px 0 0;
  `}
`;

const TextContainer = styled.div<{
  hasDescription: boolean;
  $disabled: boolean;
}>`
  ${({ hasDescription, theme, $disabled }) => css`
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    word-wrap: break-word;
    margin-top: ${hasDescription ? theme.spacing(0) : theme.spacing('5px')};
    opacity: ${$disabled ? 0.4 : 1};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const TagWrapper = styled.div`
  ${({ theme }) => css`
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `}
`;
